/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2022 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { ONE_SECOND } from "@vienna/common/src";

import { COMMAND_SYMBOL } from "src/components/Hotkeys/hotkeys";

/* -------------------------------------------------- */
/*                       Plate                        */
/* -------------------------------------------------- */

export const VIENNA_CANVAS_ID = "vienna-canvas";

export const VIENNA_TOPBAR_HEIGHT = 50;
export const PAGE_CONTENT_CONTAINER_MARGIN_TOP = 15;
export const PAGE_TOPBAR_HEIGHT = 21;
export const PAGE_TOPBAR_MARGIN = 24;

export const ONE_GRID_UNIT = 10;
export const GRID_SIZE = 10;
export const GRID_BACKGROUND_OFFSET = 4.5;
export const GRID_WIDTH_EXTRA_PADDING = 2;
export const PLATE_PADDING = 24;

export const CODE_LANG_MENU_ID = "vienna-code-lang-menu";

/* -------------------------------------------------- */
/*                       Block                        */
/* -------------------------------------------------- */

export const MASONRY_BLOCK_WIDTH = 262;
export const BLOCK_TOPBAR_HEIGHT = 20;

export const DEFAULT_PLATE_BLOCK_HEIGHT = 400;
export const DEFAULT_PLATE_BLOCK_WIDTH = 200;

export const DEFAULT_FILE_BLOCK_WIDTH = 300;
export const DEFAULT_FILE_BLOCK_HEIGHT = 300;

export const DEFAULT_THUMBNAIL_WIDTH = 120;

export const FEED_BLOCK_WIDTH = 350;

export const NO_SCROLL_TYPES = ["image", "linkEmbed", "twitterEmbed", "spotifyEmbed", "youtubeEmbed"]; // block types that should have overflow hidden
export const SMALL_OVERLAY_TYPES = ["twitterEmbed", "spotifyEmbed", "youtubeEmbed"]; // block types that should use the small overlay even in the feed

export const ACCEPTED_FILE_TYPES = {
  "image/*": [".png", ".gif", ".jpeg", ".jpg", ".ico", ".avif", ".webp", ".svg", ".tif", ".tiff", ".heic"],
  "audio/*": [".mp3", ".wav"],
  "text/*": [".txt", ".csv", ".tsv", ".json", ".xml", ".html", ".css", ".js", ".ics"],
  "video/*": [".mp4", ".webm", ".mov"],
  "application/pdf": [".pdf"],
  "application/zip": [".zip"],
  "application/doc": [".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx"],
};

/* -------------------------------------------------- */
/*                       BUCKETS                      */
/* -------------------------------------------------- */

export const FILE_BUCKET = `${process.env.ENVIRONMENT}-vht-file-bucket`;
export const IMAGE_BUCKET = `${process.env.ENVIRONMENT}-vht-image-bucket`;
export const AVATAR_BUCKET = `${process.env.ENVIRONMENT === "prod" ? "prod" : "dev"}-vht-avatar-bucket`;

/* -------------------------------------------------- */
/*                       Routes                       */
/* -------------------------------------------------- */

export enum AuthLevel {
  Guest = "Guest", // is not authed
  Authorized = "FullAuthorization", // is authed
  Onboarding = "Onboarding",
}

export enum AppRoutes {
  Root = "/",
  // META ROUTES
  Meta = "/meta",
  Waitlist = "/meta/waitlist",
  WaitlistSuccess = "/meta/waitlist/success",
  Download = "/meta/download",
  Blog = "/meta/blog",
  Jobs = "/meta/jobs",
  // VIENNA SCRIBE ROUTE
  ScribeHome = "/meta/scribe",
  ScribePrivacy = "/meta/scribe/privacy",
  ScribeFAQ = "/meta/scribe/faq",
  ScribeAccuracy = "/meta/scribe/accuracy",
  // BLOG POSTS
  BlogSignupWithUrbit = "/meta/blog/signup-with-urbit",
  BlogFunding2022 = "/meta/blog/funding-2022",
  BlogTheYear2022 = "/meta/blog/the-year-2022",
  // AUTH ROUTES
  Signin = "/signin",
  SigninUrbit = "/signin-urbit",
  SigninEmail = "/signin-email",
  ResetPassword = "/reset-password",
  RequestResetPassword = "/request-reset",
  Logout = "/logout",
  // SIGNUP ROUTES
  Register = "/signup/register",
  CheckSignInKey = "/check-key",
  Onboarding = "/signup/onboarding",
  // APP ROUTES
  Home = "/home/[tab]",
  HomeMine = "/home/mine",
  Notifications = "/notifications",
  Settings = "/settings",
  Explore = "/explore",
  Plate = "/plate/[username]/[pageSlug]",
  Profile = "/user/[username]",
}

/* -------------------------------------------------- */
/*                      SHORTCUTS                     */
/* -------------------------------------------------- */
export const GENERAL_SHORTCUTS = [
  {
    command: `${COMMAND_SYMBOL}K`,
    label: "Toggle Search",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/cmd-k.gif",
  },
  {
    command: `${COMMAND_SYMBOL}+Shift+K`,
    label: "Toggle Command Palette",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/cmd-shift-k.gif",
  },
  {
    command: `${COMMAND_SYMBOL}+Shift+Zero (Desktop Only)`,
    label: "Change the Theme",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/cmd-shift-l.gif",
  },
  {
    command: `${COMMAND_SYMBOL}N`,
    label: "Create New Plate (Desktop Only)",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/cmd-n.gif",
  },
];

export const BLOCK_SHORTCUTS = [
  // {
  //   command: `${COMMAND_SYMBOL}B`,
  //   label: "Create New Block",
  //   imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/cmd-b.gif",
  // },
  {
    command: "(↑/→/↓/←)",
    label: "Move Block",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/arrows.gif",
  },
  {
    command: "Shift + (↑/→/↓/←)",
    label: "Move Block Quickly",
    imageUrl: "https://vht-tutorial-gifs.s3.amazonaws.com/shift-arrows.gif",
  },
  {
    command: "Shift + Resize / Create",
    label: "Lock Aspect Ratio",
    imageUrl: "",
  },
  {
    command: "Shift + Drag",
    label: "Lock Direction",
    imageUrl: "",
  },
  {
    command: "Option + Drag",
    label: "Duplicate Block",
    imageUrl: "",
  },
];

export const TOAST_DEFAULT_ARGS = {
  position: "bottom-right" as const,
  autoClose: 3 * ONE_SECOND,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};
